import { useState, useEffect } from "react";
import { Reagent } from "@/services/reagentService";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/contexts/auth";
import { fetchMyReagents } from "@/services/myReagents";
import { createSwapRequest } from "@/services/swapRequests";

export function useSwapRequestForm(reagent: Reagent) {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [offerType, setOfferType] = useState<"reagent" | "credits">("credits");
  const [message, setMessage] = useState("");
  const [creditAmount, setCreditAmount] = useState(50);
  const [userReagents, setUserReagents] = useState<Reagent[]>([]);
  const [isLoadingReagents, setIsLoadingReagents] = useState(false);
  const [selectedReagentId, setSelectedReagentId] = useState<string | null>(null);
  const [insufficientCredits, setInsufficientCredits] = useState(false);

  useEffect(() => {
    if (offerType === "credits" && profile) {
      setInsufficientCredits(profile.credit_balance < creditAmount);
    } else {
      setInsufficientCredits(false);
    }
  }, [creditAmount, profile, offerType]);

  useEffect(() => {
    const loadUserReagents = async () => {
      if (offerType === "reagent") {
        setIsLoadingReagents(true);
        try {
          const myReagents = await fetchMyReagents();
          const filteredReagents = myReagents.filter(r => r.id !== reagent.id);
          setUserReagents(filteredReagents);
          
          if (filteredReagents.length > 0 && !selectedReagentId) {
            setSelectedReagentId(filteredReagents[0].id);
          }
        } catch (error) {
          console.error("Error loading user reagents:", error);
          toast.error("Failed to load your reagents");
        } finally {
          setIsLoadingReagents(false);
        }
      }
    };

    loadUserReagents();
  }, [offerType, reagent.id, selectedReagentId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (isSubmitting) {
      toast.info("Your request is already being processed");
      return;
    }
    
    if (offerType === "reagent" && !selectedReagentId) {
      toast.error("Please select a reagent to offer");
      return;
    }
    
    if (!message.trim()) {
      toast.error("Please include a message for the reagent owner");
      return;
    }
    
    if (offerType === "credits" && insufficientCredits) {
      toast.error(`You don't have enough credits (${profile?.credit_balance || 0} available)`);
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      console.log("Submitting swap request with:", {
        reagentId: reagent.id,
        offerType, 
        creditAmount: offerType === "credits" ? creditAmount : undefined,
        offeredReagentId: offerType === "reagent" ? selectedReagentId : undefined,
        numberOfPackages: reagent.number_of_packages
      });
      
      const result = await createSwapRequest({
        reagentId: reagent.id,
        message,
        offerType,
        creditAmount: offerType === "credits" ? creditAmount : undefined,
        offeredReagentId: offerType === "reagent" ? selectedReagentId : undefined
      });
      
      if (result.success) {
        if (result.alreadyExists) {
          toast.info("You already have a swap request for this reagent. Opening it.");
        } else {
          toast.success("Swap request sent successfully!");
        }
        navigate(`/swap-request/${result.id}`);
      } else {
        toast.error(result.error || "Failed to create swap request");
      }
    } catch (error) {
      console.error("Error submitting swap request:", error);
      toast.error("An unexpected error occurred");
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    offerType,
    setOfferType,
    message,
    setMessage,
    creditAmount,
    setCreditAmount,
    userReagents,
    selectedReagentId,
    setSelectedReagentId,
    isLoadingReagents,
    insufficientCredits,
    isSubmitting,
    handleSubmit,
    profile
  };
}
